export interface CategoryEditorListenerHandler {
    handleOpenClick(event: Event): void;
    handleOverlayClick(event: Event): void;
    handleCloseClick(event: Event): void;
    handleTabParentClick(event: Event): void;
    handleTabChildClick(event: Event): void;
    handleTabReviewClick(event: Event): void;
    onParentPanelInput(event: Event): void;
    onChildPanelInput(event: Event): void;
    onChildPanelChange(event: Event): void;
    onReviewPanelInput(event: Event): void;
    onReviewPanelChange(event: Event): void;
    onReviewPanelClick(event: Event): void;
    onParentPanelClick(event: Event): void;
    onChildPanelClick(event: Event): void;
    handleFooterAddClick(event: Event): void;
    handleAddEntryModalClick(event: Event): void;
    handleAddEntryModalInput(event: Event): void;
    handleAddEntryModalChange(event: Event): void;
    handleAddEntrySubmit(event: Event): Promise<void> | void;
    handleAddEntryClose(event: Event): void;
    handleQuestionOptionModalClick(event: Event): void;
    handleQuestionOptionSubmit(event: Event): Promise<void> | void;
    handleQuestionOptionClose(event: Event): void;
    handleSaveClick(event: Event): Promise<void> | void;
}

export interface CategoryEditorListenerElements {
    overlayEl: HTMLElement;
    tabParentEl: HTMLElement;
    tabChildEl: HTMLElement;
    tabReviewEl: HTMLElement;
    panelParentEl: HTMLElement;
    panelChildEl: HTMLElement;
    panelReviewEl: HTMLElement;
    saveBtnEl: HTMLElement;
    addBtnEl: HTMLElement;
    addEntryModalEl: HTMLElement;
    addEntrySubmitBtnEl: HTMLElement;
    addEntryCloseBtnEls: HTMLElement[];
    questionOptionModalEl: HTMLElement;
    questionOptionSubmitBtnEl: HTMLElement;
    questionOptionCloseBtnEls: HTMLElement[];
    closeBtnEls: HTMLElement[];
    openBtnEls: HTMLElement[];
}

export class CategoryEditorListener {
    public handler: CategoryEditorListenerHandler;
    public elements: CategoryEditorListenerElements;

    /**
     * @param {{
     *   handleOpenClick(event: Event): void,
     *   handleOverlayClick(event: Event): void,
     *   handleCloseClick(event: Event): void,
     *   handleTabParentClick(event: Event): void,
     *   handleTabChildClick(event: Event): void,
     *   handleTabReviewClick(event: Event): void,
     *   onParentPanelInput(event: Event): void,
     *   onChildPanelInput(event: Event): void,
     *   onChildPanelChange(event: Event): void,
     *   onReviewPanelInput(event: Event): void,
     *   onReviewPanelChange(event: Event): void,
     *   onReviewPanelClick(event: Event): void,
     *   onParentPanelClick(event: Event): void,
     *   onChildPanelClick(event: Event): void,
     *   handleFooterAddClick(event: Event): void,
     *   handleAddEntryModalClick(event: Event): void,
     *   handleAddEntryModalInput(event: Event): void,
     *   handleAddEntryModalChange(event: Event): void,
     *   handleAddEntrySubmit(event: Event): Promise<void>|void,
     *   handleAddEntryClose(event: Event): void,
     *   handleQuestionOptionModalClick(event: Event): void,
     *   handleQuestionOptionSubmit(event: Event): Promise<void>|void,
     *   handleQuestionOptionClose(event: Event): void,
     *   handleSaveClick(event: Event): Promise<void>|void,
     * }} handler
     * @param {{
     *   overlayEl: HTMLElement,
     *   tabParentEl: HTMLElement,
     *   tabChildEl: HTMLElement,
     *   tabReviewEl: HTMLElement,
     *   panelParentEl: HTMLElement,
     *   panelChildEl: HTMLElement,
     *   panelReviewEl: HTMLElement,
     *   saveBtnEl: HTMLElement,
     *   addBtnEl: HTMLElement,
     *   addEntryModalEl: HTMLElement,
     *   addEntrySubmitBtnEl: HTMLElement,
     *   addEntryCloseBtnEls: HTMLElement[],
     *   questionOptionModalEl: HTMLElement,
     *   questionOptionSubmitBtnEl: HTMLElement,
     *   questionOptionCloseBtnEls: HTMLElement[],
     *   closeBtnEls: HTMLElement[],
     *   openBtnEls: HTMLElement[]
     * }} elements
     */
    public constructor(handler: CategoryEditorListenerHandler, elements: CategoryEditorListenerElements) {
        this.handler = handler;
        this.elements = elements;
    }

    public register(): void {
        const {
            overlayEl,
            tabParentEl,
            tabChildEl,
            tabReviewEl,
            panelParentEl,
            panelChildEl,
            panelReviewEl,
            saveBtnEl,
            addBtnEl,
            addEntryModalEl,
            addEntrySubmitBtnEl,
            addEntryCloseBtnEls,
            questionOptionModalEl,
            questionOptionSubmitBtnEl,
            questionOptionCloseBtnEls,
            closeBtnEls,
            openBtnEls,
        } = this.elements;

        openBtnEls.forEach((btn) => {
            if (btn.dataset.reviewEditorBound === "1") return;
            btn.dataset.reviewEditorBound = "1";
            btn.addEventListener("click", (event) => this.handler.handleOpenClick(event));
        });

        overlayEl.addEventListener("click", (event) => this.handler.handleOverlayClick(event));

        closeBtnEls.forEach((el) => {
            el.addEventListener("click", (event) => this.handler.handleCloseClick(event));
        });

        tabParentEl.addEventListener("click", (event) => this.handler.handleTabParentClick(event));
        tabChildEl.addEventListener("click", (event) => this.handler.handleTabChildClick(event));
        if (tabReviewEl instanceof HTMLElement) {
            tabReviewEl.addEventListener("click", (event) => this.handler.handleTabReviewClick(event));
        }

        panelParentEl.addEventListener("input", (event) => this.handler.onParentPanelInput(event));
        panelChildEl.addEventListener("input", (event) => this.handler.onChildPanelInput(event));
        panelChildEl.addEventListener("change", (event) => this.handler.onChildPanelChange(event));
        if (panelReviewEl instanceof HTMLElement) {
            panelReviewEl.addEventListener("input", (event) => this.handler.onReviewPanelInput(event));
            panelReviewEl.addEventListener("change", (event) => this.handler.onReviewPanelChange(event));
            panelReviewEl.addEventListener("click", (event) => this.handler.onReviewPanelClick(event));
        }

        panelParentEl.addEventListener("click", (event) => this.handler.onParentPanelClick(event));
        panelChildEl.addEventListener("click", (event) => this.handler.onChildPanelClick(event));

        addBtnEl.addEventListener("click", (event) => this.handler.handleFooterAddClick(event));
        addEntryModalEl.addEventListener("click", (event) => this.handler.handleAddEntryModalClick(event));
        addEntryModalEl.addEventListener("input", (event) => this.handler.handleAddEntryModalInput(event));
        addEntryModalEl.addEventListener("change", (event) => this.handler.handleAddEntryModalChange(event));
        addEntrySubmitBtnEl.addEventListener("click", (event) => this.handler.handleAddEntrySubmit(event));
        addEntryCloseBtnEls.forEach((el) => {
            el.addEventListener("click", (event) => this.handler.handleAddEntryClose(event));
        });

        questionOptionModalEl.addEventListener("click", (event) => this.handler.handleQuestionOptionModalClick(event));
        questionOptionSubmitBtnEl.addEventListener("click", (event) => this.handler.handleQuestionOptionSubmit(event));
        questionOptionCloseBtnEls.forEach((el) => {
            el.addEventListener("click", (event) => this.handler.handleQuestionOptionClose(event));
        });

        saveBtnEl.addEventListener("click", (event) => this.handler.handleSaveClick(event));
    }
}
